import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { forgotPassword, resetPassword } from '../services/AuthService';

// Reads ?reset_token=... once on load (the app has no router — reset links
// land on the root URL) and scrubs it from the address bar so it can't end
// up in bookmarks or shared links.
const consumeResetToken = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('reset_token');
  if (token) {
    params.delete('reset_token');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
  }
  return token;
};

const initialResetToken = consumeResetToken();

const AuthPage = () => {
  // 'login' | 'register' | 'forgot' | 'reset'
  const [mode, setMode] = useState(initialResetToken ? 'reset' : 'login');
  const [resetToken] = useState(initialResetToken);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, register } = useAuth();

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setNotice('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(username, password);
      } else if (mode === 'register') {
        await register(username, email, password);
      } else if (mode === 'forgot') {
        const data = await forgotPassword(email);
        setNotice(data.message);
      } else if (mode === 'reset') {
        const data = await resetPassword(resetToken, password);
        setNotice(`${data.message}`);
        setPassword('');
        setMode('login');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitLabels = {
    login: { idle: 'Log In', busy: 'Logging in...' },
    register: { idle: 'Create Account', busy: 'Creating account...' },
    forgot: { idle: 'Send Reset Link', busy: 'Sending...' },
    reset: { idle: 'Set New Password', busy: 'Updating...' }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo / Title */}
        <div className="text-center mb-8">
          <img
            src="/pockedex_logo.png"
            alt="Pockedex"
            className="w-32 h-32 mx-auto mb-3 rounded-xl"
          />
          <p className="text-gray-500">Track your Pokemon card collection</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Tab Toggle — hidden on the forgot/reset screens */}
          {(mode === 'login' || mode === 'register') && (
            <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => switchMode('login')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === 'login'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Log In
              </button>
              <button
                onClick={() => switchMode('register')}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                  mode === 'register'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Sign Up
              </button>
            </div>
          )}

          {mode === 'forgot' && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Forgot your password?</h2>
              <p className="text-sm text-gray-500 mt-1">
                Enter your account email and we'll send you a reset link.
              </p>
            </div>
          )}

          {mode === 'reset' && (
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800">Choose a new password</h2>
              <p className="text-sm text-gray-500 mt-1">
                Enter a new password for your account.
              </p>
            </div>
          )}

          {/* Error / notice */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          {notice && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {notice}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {(mode === 'login' || mode === 'register') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  placeholder="Enter username"
                  required
                />
              </div>
            )}

            {(mode === 'register' || mode === 'forgot') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  placeholder="Enter email"
                  required
                />
              </div>
            )}

            {mode !== 'forgot' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {mode === 'reset' ? 'New Password' : 'Password'}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  placeholder={mode === 'login' ? 'Enter password' : '8+ characters'}
                  required
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  {submitLabels[mode].busy}
                </span>
              ) : (
                submitLabels[mode].idle
              )}
            </button>
          </form>

          {mode === 'login' && (
            <button
              onClick={() => switchMode('forgot')}
              className="mt-4 w-full text-center text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              Forgot password?
            </button>
          )}

          {(mode === 'forgot' || mode === 'reset') && (
            <button
              onClick={() => switchMode('login')}
              className="mt-4 w-full text-center text-sm text-gray-500 hover:text-gray-700 hover:underline"
            >
              ← Back to log in
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
